import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jobAPI, caseFileAPI } from '../api/api';
import { formatDateForDisplay, formatJobDueDateForDisplay, formatDateToLocalString, toISODateOrNull, parseBackendDate, isOverdueDate } from '../utils/dateUtils';
import JobForm from '../components/Jobs/JobForm';
import ActionIcon from '../components/Common/ActionIcon';
import DailyJobProgressRing from '../components/Jobs/DailyJobProgressRing';
import { canAccessOzelDosyalar, isOzelJob } from '../utils/ozelDosyaAccess';
import './JobList.css';
import { onayla } from '../utils/onayla';
import BosDurum from '../components/Common/BosDurum';
import { YENI_TASARIM } from '../utils/yeniTasarim';

const JOB_STATUS = { Pending: 0, UnderReview: 1, Approved: 2, Rejected: 3 };
const STATUS_LABELS = {
  [JOB_STATUS.Pending]: 'Yapılacak',
  [JOB_STATUS.UnderReview]: 'Kontrol Bekliyor',
  [JOB_STATUS.Approved]: 'Onaylandı',
  [JOB_STATUS.Rejected]: 'Reddedildi'
};

const getJobStatusLabel = (job) => {
  const isCompleted = job.IsCompleted ?? job.isCompleted ?? false;
  if (!isCompleted) return 'Yapılacak';
  const status = job.Status ?? job.status ?? 0;
  return STATUS_LABELS[status] ?? 'Bilinmiyor';
};

const TAB_LABELS = {
  pending: 'Yapılacaklar',
  underReview: 'Kontrol Bekliyor',
  approved: 'Onaylandı',
  rejected: 'Reddedildi',
  all: 'Tümü',
  ozel: 'Özel'
};
const getTabLabel = (filterStatus) => TAB_LABELS[filterStatus] ?? 'Tümü';

const JobList = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [caseFiles, setCaseFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [editingJob, setEditingJob] = useState(null);
  const [showJobForm, setShowJobForm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createCaseFileId, setCreateCaseFileId] = useState('');
  const [createCaseFileSearch, setCreateCaseFileSearch] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createDueDate, setCreateDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [completeModalJob, setCompleteModalJob] = useState(null);
  const [completeComment, setCompleteComment] = useState('');
  const [sortColumn, setSortColumn] = useState('dueDate'); // varsayılan: eski tarihten yeniye
  const [sortDirection, setSortDirection] = useState('asc');

  const sortedAndFilteredCreateCaseFiles = useMemo(() => {
    let list = [...caseFiles];
    // Föy No'ya göre sırala (sayısal veya metin) - Tasks sayfasındaki TaskForm ile aynı mantık
    list.sort((a, b) => {
      const aFoy = a.FoyNo ?? a.foyNo;
      const bFoy = b.FoyNo ?? b.foyNo;
      const aNum = parseInt(aFoy, 10);
      const bNum = parseInt(bFoy, 10);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
      return String(aFoy ?? '').localeCompare(String(bFoy ?? ''), 'tr');
    });
    // Müvekkil veya Föy No ile filtrele
    const search = (createCaseFileSearch || '').trim().toLowerCase();
    if (search) {
      list = list.filter((file) => {
        const muvekkil = (file.Muvekkil ?? file.muvekkil ?? '').toLowerCase();
        const foyNo = String(file.FoyNo ?? file.foyNo ?? '');
        return muvekkil.includes(search) || foyNo.toLowerCase().includes(search);
      });
    }
    return list;
  }, [caseFiles, createCaseFileSearch]);

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  useEffect(() => {
    if (filterStatus === 'ozel' && !canAccessOzelDosyalar(user)) {
      setFilterStatus('pending');
    }
  }, [user, filterStatus]);

  useEffect(() => {
    loadJobs();
    if (isAdmin()) {
      const loadOzel = filterStatus === 'ozel' && canAccessOzelDosyalar(user);
      caseFileAPI.getAll(loadOzel).then(setCaseFiles).catch(() => setCaseFiles([]));
    }
  }, [user, filterStatus]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const data = await jobAPI.getAll();
      setJobs(data || []);
    } catch (error) {
      console.error('İşler yüklenirken hata:', error);
      alert('İşler yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = useMemo(() => {
    let list = [...jobs];
    if (filterStatus === 'ozel') {
      list = list.filter(isOzelJob);
    } else {
      list = list.filter((j) => !isOzelJob(j));
      if (filterStatus === 'pending') {
        list = list.filter(j => !(j.IsCompleted ?? j.isCompleted));
      } else if (filterStatus === 'underReview') {
        list = list.filter(j => {
          const completed = j.IsCompleted ?? j.isCompleted;
          const status = j.Status ?? j.status ?? 0;
          return completed && (status === 0 || status === 1);
        });
      } else if (filterStatus === 'approved') {
        list = list.filter(j => (j.Status ?? j.status) === 2);
      } else if (filterStatus === 'rejected') {
        list = list.filter(j => (j.Status ?? j.status) === 3);
      }
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(j => {
        const foyNo = String(j.CaseFileFoyNo ?? j.caseFileFoyNo ?? '').toLowerCase();
        const muvekkil = (j.CaseFileMuvekkil ?? j.caseFileMuvekkil ?? '').toLowerCase();
        const desc = (j.Description ?? j.description ?? '').toLowerCase();
        return foyNo.includes(s) || muvekkil.includes(s) || desc.includes(s);
      });
    }
    return list;
  }, [jobs, filterStatus, search]);

  const tabCounts = useMemo(() => {
    const normal = jobs.filter((j) => !isOzelJob(j));
    const ozel = jobs.filter(isOzelJob);
    const pending = normal.filter(j => !(j.IsCompleted ?? j.isCompleted)).length;
    const underReview = normal.filter(j => {
      const completed = j.IsCompleted ?? j.isCompleted;
      const status = j.Status ?? j.status ?? 0;
      return completed && (status === 0 || status === 1);
    }).length;
    const approved = normal.filter(j => (j.Status ?? j.status) === 2).length;
    const rejected = normal.filter(j => (j.Status ?? j.status) === 3).length;
    return { pending, underReview, approved, rejected, all: normal.length, ozel: ozel.length };
  }, [jobs]);

  const sortedJobs = useMemo(() => {
    if (!sortColumn) return filteredJobs;
    const list = [...filteredJobs];
    const cmp = (a, b) => {
      switch (sortColumn) {
        case 'foyNo': {
          const va = String(a.CaseFileFoyNo ?? a.caseFileFoyNo ?? '').toLowerCase();
          const vb = String(b.CaseFileFoyNo ?? b.caseFileFoyNo ?? '').toLowerCase();
          return va.localeCompare(vb, 'tr');
        }
        case 'muvekkil': {
          const va = (a.CaseFileMuvekkil ?? a.caseFileMuvekkil ?? '').toLowerCase();
          const vb = (b.CaseFileMuvekkil ?? b.caseFileMuvekkil ?? '').toLowerCase();
          return va.localeCompare(vb, 'tr');
        }
        case 'description': {
          const va = (a.Description ?? a.description ?? '').toLowerCase();
          const vb = (b.Description ?? b.description ?? '').toLowerCase();
          return va.localeCompare(vb, 'tr');
        }
        case 'dueDate': {
          // Tarihi olmayanlar sonda; tarih varsa takvim gününe göre (eski → yeni)
          const toDay = (v) => {
            if (!v) return null;
            const s = formatDateToLocalString(v);
            return s || null;
          };
          const da = toDay(a.DueDate ?? a.dueDate);
          const db = toDay(b.DueDate ?? b.dueDate);
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return da.localeCompare(db);
        }
        case 'createdBy': {
          const va = `${a.CreatedByUserName ?? a.createdByUserName ?? ''} ${a.CreatedByUserSurname ?? a.createdByUserSurname ?? ''}`.trim().toLowerCase();
          const vb = `${b.CreatedByUserName ?? b.createdByUserName ?? ''} ${b.CreatedByUserSurname ?? b.createdByUserSurname ?? ''}`.trim().toLowerCase();
          return va.localeCompare(vb, 'tr');
        }
        case 'completedBy': {
          const va = `${a.CompletedByUserName ?? a.completedByUserName ?? ''} ${a.CompletedByUserSurname ?? a.completedByUserSurname ?? ''}`.trim().toLowerCase();
          const vb = `${b.CompletedByUserName ?? b.completedByUserName ?? ''} ${b.CompletedByUserSurname ?? b.completedByUserSurname ?? ''}`.trim().toLowerCase();
          return va.localeCompare(vb, 'tr');
        }
        case 'status': {
          const sa = (a.IsCompleted ?? a.isCompleted) ? (a.Status ?? a.status ?? 0) : -1;
          const sb = (b.IsCompleted ?? b.isCompleted) ? (b.Status ?? b.status ?? 0) : -1;
          return sa - sb;
        }
        default: return 0;
      }
    };
    list.sort((a, b) => (sortDirection === 'asc' ? 1 : -1) * cmp(a, b));
    return list;
  }, [filteredJobs, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
  };

  const SortArrow = ({ column }) => (
    <span className="job-list-sort-arrows">
      {sortColumn !== column ? (
        <><span className="job-list-sort-arrow">▲</span><span className="job-list-sort-arrow">▼</span></>
      ) : sortDirection === 'asc' ? (
        <span className="job-list-sort-arrow active">▲</span>
      ) : (
        <span className="job-list-sort-arrow active">▼</span>
      )}
    </span>
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatDateForDisplay(dateString);
  };

  const openCompleteModal = (job) => {
    setCompleteModalJob(job);
    setCompleteComment('');
  };

  const handleCompleteWithComment = async () => {
    if (!completeModalJob) return;
    const jobId = completeModalJob.Id ?? completeModalJob.id;
    const caseFileId = completeModalJob.CaseFileId ?? completeModalJob.caseFileId;
    try {
      await jobAPI.update({
        JobId: jobId,
        IsCompleted: true,
        CompletedComment: completeComment.trim() || null
      });
      await loadJobs();
      setCompleteModalJob(null);
      setCompleteComment('');
      setSelectedJob(null);
      if (caseFileId) window.dispatchEvent(new CustomEvent('jobUpdated', { detail: { caseFileId } }));
      alert('İş tamamlandı. Onaylamak için Onayla demeniz gerekir.');
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleRequestReview = async (job) => {
    const jobId = job.Id ?? job.id;
    if (!await onayla('Bu işi kontrol için talep etmek istediğinize emin misiniz?')) return;
    try {
      await jobAPI.requestReview(jobId);
      await loadJobs();
      setSelectedJob(null);
      alert('İş kontrol için talep edildi!');
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleApprove = async (job) => {
    const jobId = job.Id ?? job.id;
    const caseFileId = job.CaseFileId ?? job.caseFileId;
    if (!await onayla('Bu iş talebini onaylamak istediğinize emin misiniz?')) return;
    try {
      await jobAPI.approve(jobId);
      await loadJobs();
      setSelectedJob(null);
      if (caseFileId) window.dispatchEvent(new CustomEvent('jobUpdated', { detail: { caseFileId } }));
      alert('İş talebi onaylandı!');
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleApproveAllUnderReview = async () => {
    const toApprove = jobs.filter((j) => {
      if (isOzelJob(j)) return false;
      const completed = j.IsCompleted ?? j.isCompleted;
      const status = j.Status ?? j.status ?? 0;
      return completed && (status === 0 || status === 1);
    });
    if (toApprove.length === 0) {
      alert('Onaylanacak kontrol bekleyen iş yok.');
      return;
    }
    if (!await onayla(`${toApprove.length} iş talebini onaylamak istediğinize emin misiniz?`)) return;
    let ok = 0;
    let fail = 0;
    for (const job of toApprove) {
      try {
        await jobAPI.approve(job.Id ?? job.id);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    await loadJobs();
    setSelectedJob(null);
    alert(fail > 0 ? `${ok} iş onaylandı, ${fail} işte hata oluştu.` : `${ok} iş onaylandı.`);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      alert('Lütfen red nedeni girin.');
      return;
    }
    if (!showRejectModal) return;
    try {
      await jobAPI.reject(showRejectModal, rejectionReason, newDueDate || null);
      await loadJobs();
      setShowRejectModal(null);
      setSelectedJob(null);
      setRejectionReason('');
      setNewDueDate('');
      alert('İş talebi reddedildi.');
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (job) => {
    const currentUserId = user?.Id ?? user?.id;
    const createdByUserId = job.CreatedByUserId ?? job.createdByUserId;
    if (!isAdmin() && createdByUserId !== currentUserId) {
      alert('Bu işi sadece oluşturan kullanıcı veya admin düzenleyebilir.');
      return;
    }
    setEditingJob(job);
    setShowJobForm(true);
  };

  const handleDelete = async (job) => {
    const currentUserId = user?.Id ?? user?.id;
    const createdByUserId = job.CreatedByUserId ?? job.createdByUserId;
    if (!isAdmin() && createdByUserId !== currentUserId) {
      alert('Bu işi sadece oluşturan kullanıcı veya admin silebilir.');
      return;
    }
    if (!await onayla('Bu işi silmek istediğinize emin misiniz?')) return;
    const jobId = job.Id ?? job.id;
    const caseFileId = job.CaseFileId ?? job.caseFileId;
    try {
      await jobAPI.delete(jobId);
      await loadJobs();
      if (caseFileId) window.dispatchEvent(new CustomEvent('jobUpdated', { detail: { caseFileId } }));
      alert('İş silindi.');
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleJobFormSubmit = async (data) => {
    if (!editingJob) return;
    setSubmitting(true);
    try {
      await jobAPI.update({
        JobId: editingJob.Id ?? editingJob.id,
        Description: data.Description,
        DueDate: toISODateOrNull(data.DueDate && /^\d{4}-\d{2}-\d{2}$/.test(data.DueDate) ? `${data.DueDate}T23:59` : data.DueDate)
      });
      await loadJobs();
      setShowJobForm(false);
      setEditingJob(null);
      setSelectedJob(null);
      alert('İş güncellendi.');
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    if (loading || sortedJobs.length === 0) {
      alert('Yazdırılacak liste boş.');
      return;
    }
    const esc = (s) => {
      if (s == null || s === '') return '';
      const t = String(s);
      return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    };
    const title = `İş Listesi – ${getTabLabel(filterStatus)}`;
    const thead = `
      <tr>
        <th>Föy No</th>
        <th>Müvekkil</th>
        <th>Açıklama</th>
        <th>Son Tarih</th>
        <th>Oluşturan</th>
        <th>İşi Yapan</th>
        <th>Durum</th>
      </tr>`;
    // Yazdırma için son tarihe göre azalan sırada sırala (en yakın tarih en üstte)
    const jobsForPrint = [...sortedJobs].sort((a, b) => {
      const dateA = parseBackendDate(a.DueDate ?? a.dueDate)?.getTime() || 0;
      const dateB = parseBackendDate(b.DueDate ?? b.dueDate)?.getTime() || 0;
      return dateB - dateA; // Azalan sıra: en yeni tarih (en büyük timestamp) en üstte
    });
    const rows = jobsForPrint.map((job) => {
      const foyNo = job.CaseFileFoyNo ?? job.caseFileFoyNo ?? '-';
      const muvekkil = job.CaseFileMuvekkil ?? job.caseFileMuvekkil ?? '-';
      const description = job.Description ?? job.description ?? '-';
      const dueDate = job.DueDate ?? job.dueDate;
      const createdBy = `${job.CreatedByUserName ?? job.createdByUserName ?? ''} ${job.CreatedByUserSurname ?? job.createdByUserSurname ?? ''}`.trim() || '-';
      const completedBy = `${job.CompletedByUserName ?? job.completedByUserName ?? ''} ${job.CompletedByUserSurname ?? job.completedByUserSurname ?? ''}`.trim() || '-';
      const statusLabel = getJobStatusLabel(job);
      const dueStr = dueDate ? formatJobDueDateForDisplay(dueDate) : '-';
      return `<tr>
        <td>${esc(foyNo)}</td>
        <td>${esc(muvekkil)}</td>
        <td>${esc(description)}</td>
        <td>${esc(dueStr)}</td>
        <td>${esc(createdBy)}</td>
        <td>${esc(completedBy)}</td>
        <td>${esc(statusLabel)}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
      body { font-family: Arial, sans-serif; font-size: 12px; padding: 16px; color: #333; }
      h1 { margin: 0 0 12px 0; font-size: 18px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; }
      th { background: #455a64; color: #fff; font-weight: 600; }
      tr:nth-child(even) { background: #f9f9f9; }
    </style></head><body><h1>${esc(title)}</h1><table><thead>${thead}</thead><tbody>${rows}</tbody></table></body></html>`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Açılır pencere engellendi. Lütfen yazdırma için izin verin.');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 250);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!createCaseFileId || !createDescription.trim()) {
      alert('Lütfen dosya seçin ve açıklama girin.');
      return;
    }
    setSubmitting(true);
    try {
      await jobAPI.create({
        CaseFileId: parseInt(createCaseFileId, 10),
        Description: createDescription.trim(),
        DueDate: createDueDate ? toISODateOrNull(createDueDate.includes('T') ? createDueDate : `${createDueDate}T23:59`) : null
      });
      await loadJobs();
      setShowCreateModal(false);
      setCreateCaseFileId('');
      setCreateDescription('');
      setCreateDueDate('');
      alert('İş oluşturuldu.');
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="job-list-page">
      <div className="job-list-header">
        <h1>İş Listesi</h1>
        {isAdmin() && (
          <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Yeni İş Ekle
          </button>
        )}
      </div>

      <div className="job-list-toolbar">
        <div className="job-list-toolbar-left">
          <div className="job-list-filters">
            <input
              type="text"
              className="form-control job-list-search"
              placeholder="Föy No, müvekkil veya açıklama ile ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="job-list-tabs-row">
            <div className="job-list-tabs">
              <button
                type="button"
                className={`job-list-tab ${filterStatus === 'pending' ? 'active' : ''}`}
                onClick={() => setFilterStatus('pending')}
              >
                Yapılacaklar <span className="job-list-tab-count">({tabCounts.pending})</span>
              </button>
              <button
                type="button"
                className={`job-list-tab ${filterStatus === 'underReview' ? 'active' : ''}`}
                onClick={() => setFilterStatus('underReview')}
              >
                Kontrol Bekliyor <span className="job-list-tab-count">({tabCounts.underReview})</span>
              </button>
              {isAdmin() && filterStatus === 'underReview' && tabCounts.underReview > 0 && (
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
                  onClick={handleApproveAllUnderReview}
                >
                  Tümünü Onayla
                </button>
              )}
              <button
                type="button"
                className={`job-list-tab ${filterStatus === 'approved' ? 'active' : ''}`}
                onClick={() => setFilterStatus('approved')}
              >
                Onaylandı <span className="job-list-tab-count">({tabCounts.approved})</span>
              </button>
              <button
                type="button"
                className={`job-list-tab ${filterStatus === 'rejected' ? 'active' : ''}`}
                onClick={() => setFilterStatus('rejected')}
              >
                Reddedildi <span className="job-list-tab-count">({tabCounts.rejected})</span>
              </button>
              <button
                type="button"
                className={`job-list-tab ${filterStatus === 'all' ? 'active' : ''}`}
                onClick={() => setFilterStatus('all')}
              >
                Tümü <span className="job-list-tab-count">({tabCounts.all})</span>
              </button>
              {canAccessOzelDosyalar(user) && (
                <button
                  type="button"
                  className={`job-list-tab ${filterStatus === 'ozel' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('ozel')}
                >
                  Özel <span className="job-list-tab-count">({tabCounts.ozel || 0})</span>
                </button>
              )}
            </div>
            <button type="button" className="job-list-print-btn no-print" onClick={handlePrint} title="Seçili listeyi yazdır">
              Yazdır
            </button>
          </div>
        </div>
        <aside className="job-list-toolbar-right">
          <DailyJobProgressRing jobs={jobs} size="md" className="job-list-day-ring" />
        </aside>
      </div>

      {loading ? (
        <div className="job-list-loading">Yükleniyor...</div>
      ) : filteredJobs.length === 0 ? (
        YENI_TASARIM
          ? <BosDurum simge="liste" baslik="Bu listede iş yok" metin="Seçili sekmede gösterilecek iş bulunmuyor. Başka bir sekmeye geçebilirsiniz." />
          : <div className="job-list-empty">İş bulunamadı.</div>
      ) : (
        <div className="job-list-table-container">
          <table className="job-list-table">
            <thead>
              <tr>
                <th className="job-list-th-sortable" onClick={() => handleSort('foyNo')}><span className="job-list-th-content"><span>Föy No</span><SortArrow column="foyNo" /></span></th>
                <th className="job-list-th-sortable" onClick={() => handleSort('muvekkil')}><span className="job-list-th-content"><span>Müvekkil</span><SortArrow column="muvekkil" /></span></th>
                <th className="job-list-th-sortable" onClick={() => handleSort('description')}><span className="job-list-th-content"><span>Açıklama</span><SortArrow column="description" /></span></th>
                <th className="job-list-th-sortable" onClick={() => handleSort('dueDate')}><span className="job-list-th-content"><span>Son Tarih</span><SortArrow column="dueDate" /></span></th>
                <th className="job-list-th-sortable" onClick={() => handleSort('createdBy')}><span className="job-list-th-content"><span>Oluşturan</span><SortArrow column="createdBy" /></span></th>
                <th className="job-list-th-sortable" onClick={() => handleSort('completedBy')}><span className="job-list-th-content"><span>İşi Yapan</span><SortArrow column="completedBy" /></span></th>
                <th className="job-list-th-sortable" onClick={() => handleSort('status')}><span className="job-list-th-content"><span>Durum</span><SortArrow column="status" /></span></th>
                <th className="job-list-col-actions">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map((job) => {
                const jobId = job.Id ?? job.id;
                const caseFileId = job.CaseFileId ?? job.caseFileId;
                const foyNo = job.CaseFileFoyNo ?? job.caseFileFoyNo;
                const muvekkil = job.CaseFileMuvekkil ?? job.caseFileMuvekkil;
                const description = job.Description ?? job.description;
                const dueDate = job.DueDate ?? job.dueDate;
                const isCompleted = job.IsCompleted ?? job.isCompleted ?? false;
                const status = job.Status ?? job.status ?? 0;
                const createdBy = `${job.CreatedByUserName ?? job.createdByUserName ?? ''} ${job.CreatedByUserSurname ?? job.createdByUserSurname ?? ''}`.trim();
                const completedBy = `${job.CompletedByUserName ?? job.completedByUserName ?? ''} ${job.CompletedByUserSurname ?? job.completedByUserSurname ?? ''}`.trim();
                const currentUserId = user?.Id ?? user?.id;
                const createdByUserId = job.CreatedByUserId ?? job.createdByUserId;
                const completedByUserId = job.CompletedByUserId ?? job.completedByUserId;
                const canEditDelete = isAdmin() || createdByUserId === currentUserId;
                const canComplete = !isCompleted && (isAdmin() || true);
                const canRequestReview = isCompleted && status === 0 && completedByUserId === currentUserId;
                const canApproveReject = isAdmin() && isCompleted && (status === 0 || status === 1);

                let isOverdue = false;
                if (dueDate && !isCompleted) {
                  isOverdue = isOverdueDate(dueDate);
                }

                return (
                  <tr key={jobId} onClick={() => setSelectedJob(job)} className="job-list-row-clickable">
                    <td onClick={(e) => e.stopPropagation()}>
                      {foyNo != null && caseFileId ? (
                        <Link to={isOzelJob(job) ? `/ozel-dosyalar/${caseFileId}` : `/case-files/${caseFileId}`} className="job-list-link">{foyNo}</Link>
                      ) : (
                        foyNo ?? '-'
                      )}
                    </td>
                    <td className="job-list-muvekkil" title={muvekkil ?? ''}>{muvekkil ?? '-'}</td>
                    <td className="job-list-desc">{description ?? '-'}</td>
                    <td>
                      <span className={isOverdue ? 'job-list-overdue' : ''}>{formatJobDueDateForDisplay(dueDate)}</span>
                    </td>
                    <td>{createdBy || '-'}</td>
                    <td>{completedBy || '-'}</td>
                    <td>
                      <span className={`job-list-badge job-list-badge-${isCompleted ? 'done' : 'pending'}`}>
                        {getJobStatusLabel(job)}
                      </span>
                    </td>
                    <td className="job-list-col-actions" onClick={(e) => e.stopPropagation()}>
                      <div className="job-list-actions">
                        {canComplete && (
                          <ActionIcon type="complete" title="Tamamla" onClick={() => openCompleteModal(job)} />
                        )}
                        {canRequestReview && (
                          <ActionIcon type="review" title="Talep Et" onClick={() => handleRequestReview(job)} />
                        )}
                        {canApproveReject && (
                          <>
                            <ActionIcon type="approve" title="Onayla" onClick={() => handleApprove(job)} />
                            <ActionIcon type="reject" title="Reddet" onClick={() => setShowRejectModal(jobId)} />
                          </>
                        )}
                        {canEditDelete && (
                          <>
                            <ActionIcon type="edit" title="Düzenle" onClick={() => handleEdit(job)} />
                            <ActionIcon type="delete" title="Sil" onClick={() => handleDelete(job)} />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedJob && (
        <div className="job-list-detail-overlay" onClick={() => setSelectedJob(null)}>
          <div className="job-list-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="job-list-detail-header">
              <h2>İş Detayı</h2>
              <button type="button" className="job-list-detail-close" onClick={() => setSelectedJob(null)} aria-label="Kapat">×</button>
            </div>
            <div className="job-list-detail-body">
              {(() => {
                const j = selectedJob;
                const jobId = j.Id ?? j.id;
                const caseFileId = j.CaseFileId ?? j.caseFileId;
                const foyNo = j.CaseFileFoyNo ?? j.caseFileFoyNo;
                const muvekkil = j.CaseFileMuvekkil ?? j.caseFileMuvekkil;
                const description = j.Description ?? j.description;
                const dueDate = j.DueDate ?? j.dueDate;
                const isCompleted = j.IsCompleted ?? j.isCompleted ?? false;
                const status = j.Status ?? j.status ?? 0;
                const createdBy = `${j.CreatedByUserName ?? j.createdByUserName ?? ''} ${j.CreatedByUserSurname ?? j.createdByUserSurname ?? ''}`.trim();
                const completedBy = `${j.CompletedByUserName ?? j.completedByUserName ?? ''} ${j.CompletedByUserSurname ?? j.completedByUserSurname ?? ''}`.trim();
                const createdDate = j.CreatedDate ?? j.createdDate;
                const completedDate = j.CompletedDate ?? j.completedDate;
                const currentUserId = user?.Id ?? user?.id;
                const createdByUserId = j.CreatedByUserId ?? j.createdByUserId;
                const completedByUserId = j.CompletedByUserId ?? j.completedByUserId;
                const canEditDelete = isAdmin() || createdByUserId === currentUserId;
                const canComplete = !isCompleted;
                const canRequestReview = isCompleted && status === 0 && completedByUserId === currentUserId;
                const canApproveReject = isAdmin() && isCompleted && (status === 0 || status === 1);
                let isOverdue = false;
                if (dueDate && !isCompleted) {
                  isOverdue = isOverdueDate(dueDate);
                }
                return (
                  <>
                    <div className="job-list-detail-row">
                      <span className="job-list-detail-label">Föy No</span>
                      <span className="job-list-detail-value">
                        {foyNo != null && caseFileId ? (
                          <Link to={isOzelJob(selectedJob) ? `/ozel-dosyalar/${caseFileId}` : `/case-files/${caseFileId}`} className="job-list-link" onClick={() => setSelectedJob(null)}>{foyNo}</Link>
                        ) : (
                          foyNo ?? '-'
                        )}
                      </span>
                    </div>
                    <div className="job-list-detail-row">
                      <span className="job-list-detail-label">Müvekkil</span>
                      <span className="job-list-detail-value">{muvekkil ?? '-'}</span>
                    </div>
                    <div className="job-list-detail-row">
                      <span className="job-list-detail-label">Açıklama</span>
                      <span className="job-list-detail-value job-list-detail-desc">{description ?? '-'}</span>
                    </div>
                    <div className="job-list-detail-row">
                      <span className="job-list-detail-label">Son Tarih</span>
                      <span className={`job-list-detail-value ${isOverdue ? 'job-list-overdue' : ''}`}>{formatJobDueDateForDisplay(dueDate)}</span>
                    </div>
                    <div className="job-list-detail-row">
                      <span className="job-list-detail-label">Oluşturan</span>
                      <span className="job-list-detail-value">{createdBy || '-'}</span>
                    </div>
                    {createdDate && (
                      <div className="job-list-detail-row">
                        <span className="job-list-detail-label">Oluşturma Tarihi</span>
                        <span className="job-list-detail-value">{formatDate(createdDate)}</span>
                      </div>
                    )}
                    <div className="job-list-detail-row">
                      <span className="job-list-detail-label">İşi Yapan</span>
                      <span className="job-list-detail-value">{completedBy || '-'}</span>
                    </div>
                    {completedDate && (
                      <div className="job-list-detail-row">
                        <span className="job-list-detail-label">Tamamlanma Tarihi</span>
                        <span className="job-list-detail-value">{formatDate(completedDate)}</span>
                      </div>
                    )}
                    {(j.CompletedComment ?? j.completedComment) && (
                      <div className="job-list-detail-row">
                        <span className="job-list-detail-label">Tamamlanma Yorumu</span>
                        <span className="job-list-detail-value job-list-detail-desc">{j.CompletedComment ?? j.completedComment}</span>
                      </div>
                    )}
                    <div className="job-list-detail-row">
                      <span className="job-list-detail-label">Durum</span>
                      <span className={`job-list-badge job-list-badge-${isCompleted ? 'done' : 'pending'}`}>
                        {getJobStatusLabel(j)}
                      </span>
                    </div>
                    <div className="job-list-detail-actions">
                      {canComplete && (
                        <button type="button" className="btn job-list-btn-tamamla" onClick={() => openCompleteModal(j)}>
                          Tamamla
                        </button>
                      )}
                      {canRequestReview && (
                        <button type="button" className="btn btn-secondary" onClick={() => handleRequestReview(j)}>
                          Talep Et
                        </button>
                      )}
                      {canApproveReject && (
                        <>
                          <button type="button" className="btn btn-success" onClick={() => handleApprove(j)}>
                            Onayla
                          </button>
                          <button type="button" className="btn btn-danger" onClick={() => setShowRejectModal(jobId)}>
                            Reddet
                          </button>
                        </>
                      )}
                      {canEditDelete && (
                        <>
                          <button type="button" className="btn job-list-btn-duzenle" onClick={() => { setEditingJob(j); setShowJobForm(true); setSelectedJob(null); }}>
                            Düzenle
                          </button>
                          <button type="button" className="btn btn-danger" onClick={() => handleDelete(j)}>
                            Sil
                          </button>
                        </>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {showJobForm && (
        <JobForm
          job={editingJob}
          onSubmit={handleJobFormSubmit}
          onCancel={() => { setShowJobForm(false); setEditingJob(null); }}
          onDelete={editingJob ? () => handleDelete(editingJob) : undefined}
          onAssignedAsTask={async () => {
            await loadJobs();
          }}
          loading={submitting}
        />
      )}

      {showRejectModal && (
        <div className="job-list-modal-overlay" onClick={() => { setShowRejectModal(null); setRejectionReason(''); setNewDueDate(''); }}>
          <div className="job-list-modal" onClick={(e) => e.stopPropagation()}>
            <h3>İş Talebini Reddet</h3>
            <div className="form-group">
              <label>Red Nedeni *</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                placeholder="Red nedeni..."
                required
              />
            </div>
            <div className="form-group">
              <label>Yeni Son Tarih (Opsiyonel)</label>
              <input
                type="date"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                className="form-control"
              />
            </div>
            <div className="job-list-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setShowRejectModal(null); setRejectionReason(''); setNewDueDate(''); }}>İptal</button>
              <button type="button" className="btn btn-danger" onClick={handleRejectSubmit}>Reddet</button>
            </div>
          </div>
        </div>
      )}

      {completeModalJob && (
        <div className="job-list-modal-overlay" onClick={() => { setCompleteModalJob(null); setCompleteComment(''); }}>
          <div className="job-list-modal" onClick={(e) => e.stopPropagation()}>
            <h3>İşi Tamamla</h3>
            <div className="form-group">
              <label>Yorum (Opsiyonel)</label>
              <textarea
                className="form-control"
                value={completeComment}
                onChange={(e) => setCompleteComment(e.target.value)}
                rows={4}
                placeholder="Tamamlanırken eklemek istediğiniz yorum..."
              />
            </div>
            <div className="job-list-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setCompleteModalJob(null); setCompleteComment(''); }}>İptal</button>
              <button type="button" className="btn job-list-btn-tamamla" onClick={handleCompleteWithComment}>Tamamla</button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && isAdmin() && (
        <div className="job-list-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="job-list-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Yeni İş Ekle</h3>
            <form onSubmit={handleCreateSubmit}>
              <div className="form-group">
                <label>Dosya (Föy No) *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Müvekkil adı veya Föy No ile ara..."
                  value={createCaseFileSearch}
                  onChange={(e) => setCreateCaseFileSearch(e.target.value)}
                  style={{ marginBottom: '8px' }}
                />
                <div
                  className="task-form-casefile-list"
                  style={{
                    maxHeight: '165px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    marginTop: '4px',
                  }}
                >
                  {sortedAndFilteredCreateCaseFiles.length === 0 ? (
                    <div style={{ padding: '10px 12px', color: '#6c757d', fontSize: '12px' }}>
                      {createCaseFileSearch.trim() ? 'Eşleşen dosya yok.' : 'Dosya bulunamadı.'}
                    </div>
                  ) : (
                    sortedAndFilteredCreateCaseFiles.map((file) => {
                      const fileId = file.Id ?? file.id;
                      const foyNo = file.FoyNo ?? file.foyNo;
                      const muvekkil = file.Muvekkil ?? file.muvekkil;
                      const isSelected = String(fileId) === String(createCaseFileId);
                      return (
                        <div
                          key={fileId}
                          role="button"
                          tabIndex={0}
                          onClick={() => setCreateCaseFileId(String(fileId))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setCreateCaseFileId(String(fileId));
                            }
                          }}
                          style={{
                            padding: '8px 10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee',
                            backgroundColor: isSelected ? '#e7f3ff' : 'transparent',
                            fontWeight: isSelected ? 600 : 400,
                            fontSize: '12px',
                          }}
                        >
                          Foy No: {foyNo} - {muvekkil}
                        </div>
                      );
                    })
                  )}
                </div>
                {createCaseFileId && (() => {
                  const selected = caseFiles.find((f) => String(f.Id ?? f.id) === String(createCaseFileId));
                  if (!selected) return null;
                  return (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#0d6efd' }}>
                      Seçilen: {selected.Muvekkil ?? selected.muvekkil} (Foy No: {selected.FoyNo ?? selected.foyNo})
                    </div>
                  );
                })()}
                <input type="hidden" name="CaseFileId" value={createCaseFileId || ''} />
              </div>
              <div className="form-group">
                <label>İş Açıklaması *</label>
                <textarea
                  className="form-control"
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  rows={4}
                  placeholder="Yapılacak işin açıklaması"
                  required
                />
              </div>
              <div className="form-group">
                <label>Son Tarih (Opsiyonel)</label>
                <input
                  type="date"
                  className="form-control"
                  value={createDueDate}
                  onChange={(e) => setCreateDueDate(e.target.value)}
                />
              </div>
              <div className="job-list-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>İptal</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Kaydediliyor...' : 'Oluştur'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobList;
